import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  FormActions,
  FormGrid,
  FormSection,
  LoadingBlock,
} from "../../../shared/ui/composites";
import SchemaField from "./SchemaField";
import {
  getSchemaFields,
  readFieldValue,
  validateFields,
  writeFieldValue,
} from "../utils/schemaForm";
import { useUpdateSettingJsonMutation } from "../api/react-query";

export default function ObjectSettingEditor({ setting, allSettings = [] }) {
  const fields = useMemo(
    () => getSchemaFields(setting?.settings_schema),
    [setting?.settings_schema],
  );
  const updateMutation = useUpdateSettingJsonMutation();
  const [formValues, setFormValues] = useState({});
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState("");

  useEffect(() => {
    const data =
      setting?.settings_json && typeof setting.settings_json === "object"
        ? setting.settings_json
        : {};

    const next = {};
    fields.forEach((field) => {
      next[field.key] = readFieldValue(data, field);
    });
    setFormValues(next);
    setErrors({});
    setMessage("");
  }, [setting?.settings_key, setting?.settings_json, fields]);

  const handleChange = (field, value) => {
    setFormValues((current) => ({ ...current, [field.key]: value }));
  };

  const handleSave = async () => {
    const nextErrors = validateFields(fields, formValues);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    let nextJson =
      setting?.settings_json && typeof setting.settings_json === "object"
        ? JSON.parse(JSON.stringify(setting.settings_json))
        : {};

    fields.forEach((field) => {
      nextJson = writeFieldValue(nextJson, field, formValues[field.key]);
    });

    try {
      await updateMutation.mutateAsync({
        settingsKey: setting.settings_key,
        settings_json: nextJson,
      });
      setMessage("ذخیره با موفقیت انجام شد");
    } catch (err) {
      setMessage(err?.response?.data?.error || err.message || "خطا در ذخیره");
    }
  };

  if (!setting) {
    return <LoadingBlock message="در حال بارگذاری..." />;
  }

  if (fields.length === 0) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
        برای این تنظیم، `settings_schema` تعریف نشده است.
      </div>
    );
  }

  return (
    <FormSection title={setting.title || setting.settings_key}>
      {setting.description && (
        <p className="mb-4 text-sm text-slate-500">{setting.description}</p>
      )}

      <FormGrid>
        {fields.map((field) => (
          <SchemaField
            key={field.key}
            field={field}
            value={formValues[field.key]}
            error={errors[field.key]}
            allSettings={allSettings}
            formValues={formValues}
            onChange={(value) => handleChange(field, value)}
          />
        ))}
      </FormGrid>

      <FormActions>
        <Button
          type="button"
          variant="submit"
          onClick={handleSave}
          disabled={updateMutation.isPending}
        >
          {updateMutation.isPending ? "در حال ذخیره..." : "ذخیره تغییرات"}
        </Button>
        {message && (
          <span className="text-sm text-slate-600">{message}</span>
        )}
      </FormActions>
    </FormSection>
  );
}
